// src/components/popups/ClaimProfileModal.jsx
import React, { useState, useCallback, useEffect } from 'react';
import ReactDOM from 'react-dom';
import Cookies from 'js-cookie'; // *** ADDED: Import js-cookie ***
import api from '../../api'; // Adjust path as needed
import { useUser } from '../../context/UserContext'; // Adjust path as needed
import { useNotification } from '../../context/NotificationContext'; // Adjust path as needed
import Loader from '../Loader'; // Adjust path as needed
import SmsConsentCheckbox from '../Form/SmsConsentCheckbox';

// --- Helper function to mask email (No changes needed here) ---
const maskEmail = (email) => {
    // Basic validation
    if (!email || !email.includes('@') || email.startsWith('@') || email.endsWith('@')) {
        return 'Invalid format';
    }
    const [userPart, domainPart] = email.split('@');
    const mask = (str, keepStart = 1, keepEnd = 1) => { /* ... mask implementation ... */
        if (!str) return '';
        const totalKeep = keepStart + keepEnd;
        if (str.length <= totalKeep) {
            return str;
        }
        const maskLength = Math.max(1, str.length - totalKeep);
        const start = str.substring(0, keepStart);
        const end = keepEnd > 0 ? str.substring(str.length - keepEnd) : '';
        const maskedSection = '*'.repeat(maskLength);
        return start + maskedSection + end;
     };
     if (!domainPart.includes('.')) {
        const maskedUser = mask(userPart, 1, 1);
        const maskedDomain = mask(domainPart, 1, 0);
        return `${maskedUser}@${maskedDomain}`;
    }
    const domainParts = domainPart.split('.');
    if (domainParts.length < 2 || !domainParts[0] || !domainParts[1]) {
        return 'Invalid format';
    }
    const domainName = domainParts[0];
    const domainTld = domainParts.slice(1).join('.');
    const maskedUser = mask(userPart, 1, 1);
    const maskedDomain = mask(domainName, 1, 0);
    return `${maskedUser}@${maskedDomain}.${domainTld}`;
};


// --- Main Component ---
export default function ClaimProfileModal({ show, companyToClaim, onClose }) {
    const { user, isAuthenticated } = useUser();
    const { showNotification } = useNotification();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [claimStep, setClaimStep] = useState('verifyEmail');
    const [enteredEmail, setEnteredEmail] = useState('');
    const [smsNotificationsConsent, setSmsNotificationsConsent] = useState(false);
    const [smsMarketingConsent, setSmsMarketingConsent] = useState(false);
    const [alternativeFormData, setAlternativeFormData] = useState({
        barcode: '',
        user_details: '',
        social_media_links: '',
        contact_email: '',
        contact_phone: '',
        description: '',
    });

    // Reset state when modal is hidden or company changes
    useEffect(() => {
        if (!show) {
            setTimeout(() => {
                setClaimStep('verifyEmail');
                setError(null);
                setLoading(false);
                setEnteredEmail('');
                setSmsNotificationsConsent(false);
                setAlternativeFormData({ /* ... reset fields ... */
                    barcode: '',
                    user_details: '',
                    social_media_links: '',
                    contact_email: '',
                    contact_phone: '',
                    description: '',
                 });
            }, 300);
        }
    }, [show]);

    const companyLegalEmail = companyToClaim?.email;
    const canAttemptStandardClaim = !!companyLegalEmail;

    const canSubmit = isAuthenticated && user && companyToClaim?.id;

    // *** ADDED: Helper function to get request configuration (CSRF Token + Auth) ***
    const getRequestConfig = () => {
        const token = localStorage.getItem('authToken'); // Assuming you use localStorage for auth token
        const csrfToken = Cookies.get('csrftoken'); // Get CSRF token from cookies

        const headers = {
            'Content-Type': 'application/json',
        };
        if (token) {
            headers['Authorization'] = `Bearer ${token}`;
        }
        if (csrfToken) {
            headers['X-CSRFToken'] = csrfToken; // Add CSRF token header
        }

        return {
            headers: headers,
            withCredentials: true, // Important for session/cookie handling
        };
    };

    // --- Handlers ---
    const handleEmailVerificationSubmit = async (e) => {
        e.preventDefault();
        if (!canSubmit) {
             showNotification("Error", "Cannot submit claim. Ensure you are logged in.", null, "error");
             return;
        }
        if (!canAttemptStandardClaim) {
            setError("Company legal email is not available. Please use alternative verification.");
            showNotification("Error", "Company legal email not found. Use alternative method.", null, "error");
            return;
        }
        if (!enteredEmail) {
            setError("Please enter the company's legal email address for verification.");
            return;
        }
        if (enteredEmail.toLowerCase() !== companyLegalEmail.toLowerCase()) {
            setError("The entered email does not match the registered legal email for this company.");
            return;
        }

        setLoading(true);
        setError(null);
        try {
            console.log("Submitting standard claim for company:", companyToClaim.id);

            // *** MODIFIED: Added request config to api.post call ***
            const config = getRequestConfig();
            const response = await api.post('/claim-company-request/', {
                company_id: companyToClaim.id,
                sms_notifications_consent: smsNotificationsConsent,
                sms_marketing_consent: smsMarketingConsent,
            }, config);

            console.log('Claim request response:', response.data);
            if (response.status === 200 || response.status === 201 || response.data.status === 'success') {
                setClaimStep('submitted');
                showNotification("Claim Submitted", `Your request to claim "${companyToClaim.name}" has been submitted. Please check the company's legal email inbox for verification steps.`, null, "success");
            } else {
                 setError(response.data.message || "An unexpected error occurred.");
                 showNotification("Claim Error", response.data.message || "Failed to submit claim request.", null, "error");
            }
        } catch (err) {
            console.error('Error submitting standard claim request:', err.response ? err.response.data : err);
            // *** Handle potential CSRF error message specifically if needed ***
            let errorMsg = "An error occurred while submitting the claim.";
            if (err.response) {
                 errorMsg = err.response.data?.detail || err.response.data?.message || (err.response.status === 403 ? "Permission denied. Please ensure you are logged in and try again." : errorMsg);
            }
            setError(errorMsg);
            showNotification("Claim Error", errorMsg, null, "error");
        } finally {
            setLoading(false);
        }
    };

    const handleAlternativeFormChange = (e) => {
        const { name, value } = e.target;
        setAlternativeFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleAlternativeSubmit = async (e) => {
        e.preventDefault();
        if (!canSubmit) {
            showNotification("Error", "Cannot submit claim. Ensure you are logged in.", null, "error");
            return;
        }
        const { barcode, user_details, social_media_links, contact_email, description } = alternativeFormData;
        if (!barcode || !user_details || !social_media_links || !contact_email || !description) {
            setError("Please fill in all required fields to proceed with alternative verification.");
            return;
        }
        setLoading(true);
        setError(null);
        try {
            console.log("Submitting alternative verification claim for company:", companyToClaim.id);

            // *** MODIFIED: Added request config to api.post call ***
            const config = getRequestConfig();
            const payload = {
                company_id: companyToClaim.id,
                ...alternativeFormData,
                sms_notifications_consent: smsNotificationsConsent,
                sms_marketing_consent: smsMarketingConsent,
            };
            const response = await api.post('/claim-requests/alternative/', payload, config);

            console.log('Alternative claim request response:', response.data);
             if (response.status === 200 || response.status === 201 || response.data.status === 'success') {
                 setClaimStep('submitted');
                 showNotification("Claim Submitted", `Your verification details for "${companyToClaim.name}" have been submitted for review.`, null, "success");
             } else {
                 setError(response.data.message || "An unexpected error occurred during alternative submission.");
                 showNotification("Claim Error", response.data.message || "Failed to submit alternative claim request.", null, "error");
             }
        } catch (err) {
            console.error('Error submitting alternative claim request:', err.response ? err.response.data : err);
            // *** Handle potential CSRF error message specifically if needed ***
            let errorMsg = "An error occurred while submitting the alternative claim.";
             if (err.response) {
                 errorMsg = err.response.data?.detail || err.response.data?.message || (err.response.status === 403 ? "Permission denied. Please ensure you are logged in and try again." : errorMsg);
             }
            setError(errorMsg);
            showNotification("Claim Error", errorMsg, null, "error");
        } finally {
            setLoading(false);
        }
    };

    const handleClose = useCallback(() => {
        onClose();
    }, [onClose]);

    // --- Render Logic ---
    if (!show) {
        return null;
    }

    const maskedCompanyEmail = companyLegalEmail ? maskEmail(companyLegalEmail) : 'company\'s registered email';

    const isAlternativeFormComplete = alternativeFormData.barcode &&
                                      alternativeFormData.user_details &&
                                      alternativeFormData.social_media_links &&
                                      alternativeFormData.contact_email &&
                                      alternativeFormData.description;

    // --- CSS Styles (Keep your existing styles) ---
    const styles = `
        /* ... (Your extensive CSS styles remain here) ... */
        :root {
           --claim-modal-base-font-size: 16px; /* Base size */
           --claim-modal-scale-factor: 1.05; /* Increase factor */
        }

        .claim-modal-overlay {
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background-color: rgba(0, 0, 0, 0.65); /* Slightly darker overlay */
            display: flex;
            align-items: center; /* Center vertically */
            justify-content: center; /* Center horizontally */
            z-index: 1000;
            opacity: 0;
            animation: fadeIn 0.3s forwards;
            padding: 20px; /* Add padding for smaller screens */
            box-sizing: border-box;
        }

        .claim-modal {
            background-color: #ffffff;
            border-radius: 12px;
            box-shadow: 0 5px 20px rgba(0, 0, 0, 0.2);
            width: 100%; /* Use full width within padding */
            max-width: 600px; /* Slightly wider max-width */
            position: relative;
            display: flex; /* Use flexbox for layout */
            flex-direction: column; /* Stack header, content, footer */
            max-height: 90vh; /* Limit max height to 90% of viewport height */
            overflow: hidden; /* Hide direct overflow, content will handle scroll */
            opacity: 0;
            transform: translateY(-20px);
            animation: slideIn 0.3s 0.1s forwards;
            font-size: var(--claim-modal-base-font-size); /* Apply base font size */
        }

        .claim-modal__close-btn {
            position: absolute;
            top: 12px; /* Adjust position */
            right: 12px;
            background: none;
            border: none;
            cursor: pointer;
            padding: 8px; /* Increase clickable area */
            line-height: 1;
            z-index: 11; /* Ensure it's above content */
        }

        .claim-modal__close-btn img {
            width: 20px; /* Slightly larger icon */
            height: 20px;
            opacity: 0.6;
            transition: opacity 0.2s ease;
        }
        .claim-modal__close-btn:hover img {
             opacity: 1;
        }

        /* Make the main content area scrollable */
        .claim-modal__content {
            padding: 25px 30px; /* Adjust padding */
            overflow-y: auto; /* Enable vertical scrolling ONLY when needed */
            flex-grow: 1; /* Allow content to take available space */
            /* Add some padding at the bottom to prevent content sticking to the edge */
            padding-bottom: 40px;
        }

        .claim-modal__header {
            text-align: center;
            margin-bottom: 20px; /* Reduced margin */
            padding-bottom: 15px;
            border-bottom: 1px solid #eee;
            flex-shrink: 0; /* Prevent header from shrinking */
             padding-left: 30px; /* Match content padding */
             padding-right: 30px;
             padding-top: 25px; /* Add top padding */
        }

        .claim-modal__title {
            /* Increase font size based on scale factor */
            font-size: calc(1.6em * var(--claim-modal-scale-factor));
            font-weight: 600;
            color: #333;
            margin: 0 0 8px 0; /* Adjusted margin */
        }

        .claim-modal__subtitle {
            /* Increase font size */
            font-size: calc(1em * var(--claim-modal-scale-factor));
            color: #666;
            margin: 0;
        }

        .claim-modal__company-name {
             font-weight: bold;
             color: #007bff;
        }

        .claim-modal__form-section {
            margin-bottom: 20px; /* Consistent spacing */
        }
        .claim-modal__form-section--center {
             text-align: center;
        }

        .claim-modal__label {
            display: block;
            margin-bottom: 8px;
            font-weight: 500;
            /* Increase font size */
            font-size: calc(0.95em * var(--claim-modal-scale-factor));
            color: #555;
        }

        .claim-modal__input,
        .claim-modal__textarea {
            width: 100%;
            padding: 12px 15px;
            border: 1px solid #ccc;
            border-radius: 6px;
            /* Increase font size */
            font-size: calc(1em * var(--claim-modal-scale-factor));
            box-sizing: border-box;
            transition: border-color 0.2s ease, box-shadow 0.2s ease;
            margin-top: 2px; /* Small space between label and input */
        }

        .claim-modal__input:focus,
        .claim-modal__textarea:focus {
            outline: none;
            border-color: #007bff;
            box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.25); /* Slightly larger focus ring */
        }

         .claim-modal__textarea {
             resize: vertical;
             min-height: 100px; /* Slightly taller textarea for description */
         }

        .claim-modal__masked-company-email {
            /* Increase font size */
            font-size: calc(1.15em * var(--claim-modal-scale-factor));
            font-weight: 600;
            color: #333;
            background-color: #f0f0f0; /* Slightly different background */
            padding: 10px 14px; /* Adjusted padding */
            border-radius: 6px; /* Match input radius */
            display: inline-block;
            margin-top: 5px;
            border: 1px solid #ddd; /* Slightly darker border */
            word-break: break-all; /* Prevent long masked emails from overflowing */
        }

        .claim-modal__info-text {
             /* Increase font size */
            font-size: calc(0.95em * var(--claim-modal-scale-factor));
            color: #666; /* Slightly darker info text */
            margin-bottom: 18px; /* Adjusted spacing */
            line-height: 1.5; /* Improved line spacing */
        }
         .claim-modal__info-text--small {
             font-size: calc(0.85em * var(--claim-modal-scale-factor));
             margin-top: 15px;
             color: #777;
         }

        .claim-modal__error {
            color: #dc3545;
            background-color: #f8d7da;
            border: 1px solid #f5c6cb;
            padding: 12px 18px; /* Adjusted padding */
            border-radius: 6px;
            margin-bottom: 20px;
             /* Increase font size */
            font-size: calc(0.95em * var(--claim-modal-scale-factor));
            text-align: center;
        }

        .claim-modal__actions {
            margin-top: 25px; /* Reduced top margin */
            display: flex;
            flex-direction: column;
            gap: 12px; /* Slightly more space between buttons */
        }

        .claim-modal__content form .claim-modal__actions,
        .claim-modal__success-content .claim-modal__actions {
             padding-top: 10px; /* Add some space above buttons */
        }

        .claim-modal__button {
            padding: 14px 22px; /* Slightly larger buttons */
            border-radius: 8px; /* More rounded buttons */
             /* Increase font size */
            font-size: calc(1em * var(--claim-modal-scale-factor));
            font-weight: 500;
            cursor: pointer;
            border: none;
            transition: background-color 0.2s ease, opacity 0.2s ease, transform 0.1s ease;
            width: 100%;
            box-sizing: border-box;
            text-align: center; /* Ensure text is centered */
        }
         .claim-modal__button:active:not(:disabled) {
             transform: scale(0.98); /* Add slight press effect */
         }

        .claim-modal__button--primary {
            background-color: #007bff;
            color: white;
        }
        .claim-modal__button--primary:hover:not(:disabled) {
            background-color: #0056b3;
        }
        .claim-modal__button--primary:disabled {
            background-color: #a0cfff;
            cursor: not-allowed;
            opacity: 0.7; /* Make disabled state clearer */
        }

        .claim-modal__button--link {
            background: none;
            color: #007bff;
            text-align: center;
            padding: 10px; /* Adjusted padding */
            font-weight: normal;
             /* Increase font size */
            font-size: calc(0.95em * var(--claim-modal-scale-factor));
        }
         .claim-modal__button--link:hover:not(:disabled) {
             text-decoration: underline;
             color: #0056b3; /* Darken on hover */
         }
         .claim-modal__button--link:disabled {
             color: #6c757d;
             cursor: not-allowed;
             opacity: 0.6;
         }

        .claim-modal__success-content {
            text-align: center;
            padding: 30px 20px; /* Adjust padding */
        }

        .claim-modal__success-icon {
            width: 65px; /* Slightly larger */
            height: 65px;
            margin-bottom: 20px;
        }

        .claim-modal__success-title {
             /* Increase font size */
             font-size: calc(1.7em * var(--claim-modal-scale-factor));
             font-weight: 600;
             color: #28a745;
             margin-bottom: 15px; /* Adjusted margin */
        }
        .claim-modal__success-text {
            /* Increase font size */
            font-size: calc(1.05em * var(--claim-modal-scale-factor));
            color: #555;
            line-height: 1.6; /* More line spacing */
            margin-bottom: 30px; /* More space before button */
        }

        /* Animations */
        @keyframes fadeIn {
            from { opacity: 0; }
            to { opacity: 1; }
        }
        @keyframes slideIn {
             from { opacity: 0; transform: translateY(-20px); }
             to { opacity: 1; transform: translateY(0); }
        }

        /* Loader specific styling */
        .claim-modal .loader-container {
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background-color: rgba(255, 255, 255, 0.85); /* Slightly more opaque */
            display: flex;
            align-items: center;
            justify-content: center;
            z-index: 10;
            border-radius: 12px; /* Match modal border radius */
        }
    `;

    return ReactDOM.createPortal(
        <>
            <style>{styles}</style>
            <div className="claim-modal-overlay" onClick={handleClose}>
                <div className="claim-modal" onClick={(e) => e.stopPropagation()}>
                    {loading && <Loader isModal={true} />}
                    <button onClick={handleClose} className="claim-modal__close-btn" aria-label="Close modal">
                        <img src="/assets/icon--close-CN6QlgAA.svg" alt="Close"/>
                    </button>

                    {(claimStep === 'verifyEmail' || claimStep === 'alternativeForm') && (
                         <div className="claim-modal__header">
                             <h2 className="claim-modal__title">
                                 {claimStep === 'verifyEmail' ? 'Claim Company Profile' : 'Alternative Verification'}
                            </h2>
                             {companyToClaim && (
                                 <p className="claim-modal__subtitle">
                                     {claimStep === 'verifyEmail' ? 'Verify ownership of:' : 'Claiming:'} <span className="claim-modal__company-name">{companyToClaim.name}</span>
                                 </p>
                             )}
                         </div>
                    )}

                    <div className="claim-modal__content">
                        {claimStep === 'verifyEmail' && (
                            <form onSubmit={handleEmailVerificationSubmit}>
                                {canAttemptStandardClaim ? (
                                    <>
                                        <div className="claim-modal__form-section claim-modal__form-section--center">
                                            <p className="claim-modal__info-text">
                                                To claim this profile, please verify the official email address associated with <span className="claim-modal__company-name">{companyToClaim.name}</span>:
                                            </p>
                                            <div className="claim-modal__masked-company-email" aria-label="Masked company email">
                                                {maskedCompanyEmail}
                                            </div>
                                        </div>
                                        <div className="claim-modal__form-section">
                                            <label htmlFor="emailVerification" className="claim-modal__label">
                                                Enter the company's full legal/official email address:
                                            </label>
                                            <input
                                                type="email"
                                                id="emailVerification"
                                                name="emailVerification"
                                                value={enteredEmail}
                                                onChange={(e) => { setEnteredEmail(e.target.value); setError(null); }} // Clear error on input change
                                                placeholder="Enter the company's registered email"
                                                required
                                                className="claim-modal__input"
                                            />
                                        </div>
                                    </>
                                ) : (
                                     <div className="claim-modal__form-section claim-modal__form-section--center">
                                        <p className="claim-modal__info-text">
                                            The primary verification email for <span className="claim-modal__company-name">{companyToClaim?.name || 'this company'}</span> is not available.
                                        </p>
                                        <p className="claim-modal__info-text">
                                            Please use the alternative verification method below.
                                        </p>
                                     </div>
                                )}

                                {error && <div className="claim-modal__error">{error}</div>}

                                <SmsConsentCheckbox
                                    checked={smsNotificationsConsent}
                                    onChange={(e) => {
                                        setSmsNotificationsConsent(e.target.checked);
                                        setError(null);
                                    }}
                                />
                                <SmsConsentCheckbox
                                    name="smsMarketingConsent"
                                    checked={smsMarketingConsent}
                                    onChange={(e) => {
                                        setSmsMarketingConsent(e.target.checked);
                                        setError(null);
                                    }}
                                    variant="marketing"
                                />

                                <div className="claim-modal__actions">
                                    <button
                                        type="submit"
                                        className="claim-modal__button claim-modal__button--primary"
                                        disabled={loading || !canSubmit || !enteredEmail || !canAttemptStandardClaim}
                                    >
                                        {loading ? 'Verifying...' : 'Verify Email & Submit Claim'}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => { setClaimStep('alternativeForm'); setError(null); }}
                                        className="claim-modal__button claim-modal__button--link"
                                        disabled={loading}
                                    >
                                        {canAttemptStandardClaim ? "I don't have access to this email" : "Proceed with Alternative Verification"}
                                    </button>
                                </div>
                            </form>
                        )}

                        {claimStep === 'alternativeForm' && (
                            <form onSubmit={handleAlternativeSubmit}>
                                <p className="claim-modal__info-text">
                                    {canAttemptStandardClaim
                                      ? `Since you cannot verify via the registered email (${maskedCompanyEmail}), please provide the following details for manual review. Our team will contact you.`
                                      : `Please provide the following details for manual review to claim "${companyToClaim?.name || 'this profile'}". Our team will contact you.`}
                                </p>
                                {error && <div className="claim-modal__error">{error}</div>}

                                {/* Form fields */}
                                <div className="claim-modal__form-section">
                                    <label htmlFor="user_details" className="claim-modal__label">Your Full Name & Position:</label>
                                    <input type="text" id="user_details" name="user_details" value={alternativeFormData.user_details} onChange={handleAlternativeFormChange} placeholder="e.g., Jane Doe, Chief Legal Officer" className="claim-modal__input" required/>
                                </div>
                                <div className="claim-modal__form-section">
                                    <label htmlFor="contact_email" className="claim-modal__label">Contact Email:</label>
                                    <input type="email" id="contact_email" name="contact_email" value={alternativeFormData.contact_email} onChange={handleAlternativeFormChange} placeholder="Email address for us to contact you" className="claim-modal__input" required/>
                                </div>
                                <div className="claim-modal__form-section">
                                    <label htmlFor="contact_phone" className="claim-modal__label">Contact Phone (Optional):</label>
                                    <input type="tel" id="contact_phone" name="contact_phone" value={alternativeFormData.contact_phone} onChange={handleAlternativeFormChange} placeholder="Phone number for us to contact you" className="claim-modal__input"/>
                                </div>
                                <div className="claim-modal__form-section">
                                    <label htmlFor="social_media_links" className="claim-modal__label">Your Social Media Profiles (one per line):</label>
                                    <textarea id="social_media_links" name="social_media_links" value={alternativeFormData.social_media_links} onChange={handleAlternativeFormChange} placeholder="https://linkedin.com/in/yourprofile&#10;https://twitter.com/yourhandle" className="claim-modal__textarea" required/>
                                </div>
                                <div className="claim-modal__form-section">
                                    <label htmlFor="barcode" className="claim-modal__label">Associated Barcode (if applicable):</label>
                                    <input type="text" id="barcode" name="barcode" value={alternativeFormData.barcode} onChange={handleAlternativeFormChange} placeholder="Enter product or ID barcode" className="claim-modal__input" required/>
                                </div>
                                <div className="claim-modal__form-section">
                                    <label htmlFor="description" className="claim-modal__label">Reason for Claim / Explanation:</label>
                                    <textarea
                                        id="description"
                                        name="description"
                                        value={alternativeFormData.description}
                                        onChange={handleAlternativeFormChange}
                                        placeholder={canAttemptStandardClaim
                                            ? `Explain why you don't have access to the company's registered email (${maskedCompanyEmail}) and why you should be granted access to this profile.`
                                            : `Explain your relationship to "${companyToClaim?.name || 'this company'}" and why you should be granted access to this profile.`
                                        }
                                        className="claim-modal__textarea"
                                        rows={4}
                                        required
                                    />
                                </div>

                                <SmsConsentCheckbox
                                    checked={smsNotificationsConsent}
                                    onChange={(e) => {
                                        setSmsNotificationsConsent(e.target.checked);
                                        setError(null);
                                    }}
                                />
                                <SmsConsentCheckbox
                                    name="smsMarketingConsent"
                                    checked={smsMarketingConsent}
                                    onChange={(e) => {
                                        setSmsMarketingConsent(e.target.checked);
                                        setError(null);
                                    }}
                                    variant="marketing"
                                />

                                <div className="claim-modal__actions">
                                    <button type="submit" className="claim-modal__button claim-modal__button--primary" disabled={loading || !canSubmit || !isAlternativeFormComplete}>
                                        {loading ? 'Submitting Details...' : 'Submit Verification Details'}
                                    </button>
                                    {canAttemptStandardClaim && (
                                        <button type="button" onClick={() => { setClaimStep('verifyEmail'); setError(null); }} className="claim-modal__button claim-modal__button--link" disabled={loading}>
                                            Back to email verification
                                        </button>
                                    )}
                                </div>
                            </form>
                         )}

                        {claimStep === 'submitted' && (
                            <div className="claim-modal__success-content">
                                <img src="/assets/icon--sendok.svg" alt="Success" className="claim-modal__success-icon"/>
                                 <h2 className="claim-modal__success-title">Request Submitted!</h2>
                                 <p className="claim-modal__success-text">
                                     Your claim request for "{companyToClaim?.name}" has been received. We will review it and get back to you soon. {companyLegalEmail && enteredEmail.toLowerCase() === companyLegalEmail.toLowerCase() && 'If email verification is required, instructions have been sent to the company\'s registered email address.'}
                                     {!companyLegalEmail && 'Our team will review your details and contact you.'}
                                     {companyLegalEmail && enteredEmail.toLowerCase() !== companyLegalEmail.toLowerCase() && 'Our team will review your details and contact you.'}
                                 </p>
                                <div className="claim-modal__actions">
                                    <button type='button' onClick={handleClose} className="claim-modal__button claim-modal__button--primary">
                                        Okay
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </>,
        document.body
    );
}
